/**
 * Wire contract between the AI chat (the iframe) and the host that embeds it (the Yola editor).
 *
 * Every message, in both directions, is one event:
 *
 *   { type: 'ws:ai-chat', meta: { source, event, id? }, payload? }
 *
 * `meta.source` is the sender (`'chat'` or `'host'`), `meta.event` names what happened, and
 * `meta.id` is set only on `request` / `response`. `payload` is the event's data, left out when
 * there is none.
 *
 * | Source | `meta.event`        | `meta.id` | Payload                                                                  |
 * | ------ | ------------------- | --------- | ------------------------------------------------------------------------ |
 * | chat   | `ready`             |           | none. Sent once after load. A new `ready` means the iframe reloaded.     |
 * | chat   | `set-view`          |           | `{ isOpen?: boolean, displayMode?: DisplayMode }`                       |
 * | chat   | `request`           | yes       | `{ type: 'save', params?: object }`                                      |
 * | chat   | `site-modification` |           | `{ status: 'start' | 'in_progress' | 'end' }`                            |
 * | chat   | `reload`            |           | none. Asks the editor to reload the site from the backend. No response. |
 * | host   | `state`             |           | `HostState`: `{ isOpen, displayMode, currentPageId, hasUnsavedChanges }` |
 * | host   | `response`          | yes       | `{ type, ok: boolean, error?: string, state: HostState }`                |
 *
 * `DisplayMode` is one of `'floating' | 'left' | 'right' | 'fullscreen'`.
 *
 * Rules:
 * - `state` is the only way the editor reports its state. It always carries the full snapshot, is
 *   sent after every `ready` and whenever any field changes, and the chat replaces its stored copy
 *   with each one it receives.
 * - `request` / `response` is the only request/reply pair; new requests add a payload `type`, not
 *   a new event. `meta.id` matches a response to its request, and the editor answers each `id`
 *   exactly once, within 30 s. The response echoes `type` and carries `state` as of the moment it
 *   is sent; the chat applies that `state` before it resolves the request. `error` is a short
 *   message, localized for the user, that the chat shows as-is; it is left out when there is
 *   none. The chat may send a request without waiting for its response; the editor still
 *   answers it.
 * - `site-modification`: sent with `start` when the chat begins changing the site, `in_progress`
 *   while it is still changing it, and `end` once it is done.
 * - Each side ignores messages whose `meta.source` is its own.
 * - Security: the editor accepts a message only from the chat iframe's `contentWindow` on the chat
 *   origin; the chat accepts a message only from `window.parent`, on its pinned origin.
 * - Standalone: if the chat never receives `state`, `hasUnsavedChanges` is `false` and nothing is
 *   gated.
 *
 * One definition, mirrored on both sides: the chat keeps it in `src/modules/embed/constants.ts`
 * and `types.ts`, the editor in `ai-chat/constants/messages.js`. Both copies carry this comment.
 */

export const EMBED_MESSAGE_TYPE = 'ws:ai-chat';

export const MESSAGE_SOURCES = {
  CHAT: 'chat',
  HOST: 'host',
};

export const CHAT_EVENTS = {
  READY: 'ready',
  SET_VIEW: 'set-view',
  REQUEST: 'request',
  SITE_MODIFICATION: 'site-modification',
  RELOAD: 'reload',
};

export const HOST_EVENTS = {
  STATE: 'state',
  RESPONSE: 'response',
};

export const REQUEST_TYPES = {
  SAVE: 'save',
};

export const SITE_MODIFICATION_STATUSES = {
  START: 'start',
  IN_PROGRESS: 'in_progress',
  END: 'end',
};

export default {
  EMBED_MESSAGE_TYPE,
  MESSAGE_SOURCES,
  CHAT_EVENTS,
  HOST_EVENTS,
  REQUEST_TYPES,
  SITE_MODIFICATION_STATUSES,
};
